import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import Icon from 'components/Utils/NewIcon';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConcernRequirementModal from 'components/Clinic/ConcernRequirementModal';
import './style.scss';

const ConcernRequirementsManager = () => {
  const dispatch = useAppDispatch();
  const requirementsById = useAppSelector((state) => state.concernRequirements.byId);
  const requirementsLoaded = useAppSelector((state) => state.concernRequirements.loaded);
  const options = useAppSelector((state) => state.appointments.options);
  const concerns = useMemo(() => {
    return options.flatMap((it) => it.concerns ?? []);
  }, [options]);

  // Requirement targeted by the modal: an existing requirement to edit, or null to create a new one.
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!requirementsLoaded) {
      dispatch(api.Appointments.getConcernRequirements());
    }
  }, [dispatch, requirementsLoaded]);

  useEffect(() => {
    if (concerns.length === 0) {
      dispatch(api.Appointments.getBookingFlowOptions());
    }
  }, [dispatch, concerns.length]);

  const requirements = useMemo(
    () =>
      Object.values(requirementsById).sort((a, b) => (a.label || '').localeCompare(b.label || '')),
    [requirementsById],
  );

  // Build maps: which concerns require / grant each requirement
  const { requiredBy, grantedBy } = useMemo(() => {
    const req = {};
    const grant = {};
    concerns.forEach((concern) => {
      (concern.requirements || []).forEach((id) => {
        if (!req[id]) req[id] = [];
        req[id].push(concern);
      });
      (concern.grants_clearances || []).forEach((clearance) => {
        const id = clearance.clearance_id;
        if (!grant[id]) grant[id] = [];
        grant[id].push(concern);
      });
    });
    return { requiredBy: req, grantedBy: grant };
  }, [concerns]);

  const handleDelete = async (requirement) => {
    await dispatch(api.Appointments.deleteConcernRequirement({ id: requirement.id }));
  };

  const openCreate = () => {
    setEditingRequirement(null);
    setModalOpen(true);
  };

  const handleEdit = (requirement) => {
    setEditingRequirement(requirement);
    setModalOpen(true);
  };

  const isUsed = (id) => requiredBy[id]?.length > 0 || grantedBy[id]?.length > 0;

  return (
    <div className="concern-requirements-manager padded-box full-width">
      <div
        className="requirements-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '15px',
        }}
      >
        <div className="small-title">Clearances / Requirements</div>
        <DefaultButton name="+ Add New" className="blue" onClick={openCreate} />
      </div>
      {requirements.length === 0 && (
        <div style={{ color: '#888', fontSize: '0.9rem' }}>No requirements defined yet.</div>
      )}
      <div className="requirements-list">
        {requirements.map((requirement) => {
          const reqBy = requiredBy[requirement.id] || [];
          const gBy = grantedBy[requirement.id] || [];
          const used = isUsed(requirement.id);
          return (
            <div key={requirement.id} className="requirement-row">
              <div className="requirement-info">
                <div className="requirement-label">{requirement.label}</div>
                {requirement.description && (
                  <div className="requirement-description">{requirement.description}</div>
                )}
                {requirement.default_uses != null && (
                  <div className="requirement-meta">Default uses: {requirement.default_uses}</div>
                )}
                {requirement.default_expires_after != null && (
                  <div className="requirement-meta">
                    Default expiry: {requirement.default_expires_after.value}{' '}
                    {requirement.default_expires_after.interval}
                  </div>
                )}
                <div className="concern-lists">
                  {gBy.length > 0 && (
                    <div className="concern-list">
                      <span className="concern-list-label">Granted by:</span>
                      {gBy.map((c) => (
                        <span key={c.id} className="concern-chip grants">
                          {c.text}
                        </span>
                      ))}
                    </div>
                  )}
                  {reqBy.length > 0 && (
                    <div className="concern-list">
                      <span className="concern-list-label">Required by:</span>
                      {reqBy.map((c) => (
                        <span key={c.id} className="concern-chip requires">
                          {c.text}
                        </span>
                      ))}
                    </div>
                  )}
                  {!used && (
                    <div className="concern-list">
                      <span className="concern-list-label unused">Not used by any concern</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="requirement-actions">
                <Icon
                  src="pencil"
                  width="16px"
                  alt="Edit requirement"
                  showTooltip
                  onClick={() => handleEdit(requirement)}
                  style={{ cursor: 'pointer' }}
                />
                <Icon
                  src="trash"
                  width="16px"
                  alt={used ? 'Cannot delete: in use by concerns' : 'Delete requirement'}
                  showTooltip
                  onClick={used ? undefined : () => handleDelete(requirement)}
                  style={{
                    cursor: used ? 'not-allowed' : 'pointer',
                    opacity: used ? 0.3 : 1,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <ConcernRequirementModal
        isOpen={modalOpen}
        requirement={editingRequirement}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

export default ConcernRequirementsManager;
